/**
 * MentionList - Autocomplete list for @mentions
 *
 * Displays matching users when typing "@" in the editor.
 * Supports keyboard navigation (Up/Down/Enter/Escape).
 *
 * This component is rendered by tippy.js as a popup near the cursor
 * when the user types "@". It receives:
 * - items: List of matching users from the API
 * - command: Callback to insert the selected mention
 *
 * How it works:
 * 1. User types "@" - triggers mention suggestion
 * 2. API is called to fetch matching users
 * 3. This component renders the list
 * 4. User selects with keyboard or mouse
 * 5. command() is called with the selected user
 * 6. Mention is inserted as a styled badge
 *
 * Requirements implemented:
 * - RICH-07: @mentions for collaborator tagging
 */

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

// ============================================================
// TYPES
// ============================================================

/**
 * User data for mention suggestions.
 * Matches the API response format from /api/users/search.
 */
export interface MentionUser {
  /** Unique user ID (used as mention identifier) */
  id: string;
  /** Display name (shown in list and mention badge) */
  name: string;
  /** Email address (shown as secondary text) */
  email: string;
  /** Optional avatar URL */
  avatarUrl?: string;
}

/**
 * Props passed to MentionList by the suggestion plugin.
 */
export interface MentionListProps {
  /** List of matching users from API search */
  items: MentionUser[];
  /**
   * Callback to insert the selected mention.
   * Called with { id, label } where:
   * - id: User ID (stored in mention node data)
   * - label: Display name (shown in the editor)
   */
  command: (props: { id: string; label: string }) => void;
}

/**
 * Ref interface exposed by MentionList.
 * Used by the suggestion plugin to handle keyboard events.
 */
export interface MentionListRef {
  /**
   * Handle keyboard events for navigation and selection.
   * Returns true if the event was handled (prevents default behavior).
   */
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

// ============================================================
// COMPONENT
// ============================================================

/**
 * MentionList Component
 *
 * Renders the autocomplete dropdown for @mentions.
 * Uses forwardRef to expose keyboard handlers to the parent.
 *
 * Features:
 * - Arrow key navigation (Up/Down to select)
 * - Enter to confirm selection
 * - Escape to close
 * - Mouse hover to select
 * - Mouse click to confirm
 * - Avatar display with fallback
 * - "No users found" empty state
 */
export const MentionList = forwardRef<MentionListRef, MentionListProps>(
  ({ items, command }, ref) => {
    // Track which item is currently selected (for keyboard navigation)
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Reset selection when items change (new search results)
    useEffect(() => {
      setSelectedIndex(0);
    }, [items]);

    // --------------------------------------------------------
    // KEYBOARD HANDLERS
    // Expose these to the suggestion plugin via ref
    // --------------------------------------------------------
    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        // Arrow Up: Move selection up (wrap to bottom if at top)
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev === 0 ? items.length - 1 : prev - 1
          );
          return true;
        }

        // Arrow Down: Move selection down (wrap to top if at bottom)
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev === items.length - 1 ? 0 : prev + 1
          );
          return true;
        }

        // Enter: Confirm selection
        if (event.key === 'Enter') {
          event.preventDefault();
          const item = items[selectedIndex];
          if (item) {
            command({ id: item.id, label: item.name });
          }
          return true;
        }

        // Escape: Close the menu (handled by returning true)
        if (event.key === 'Escape') {
          event.preventDefault();
          return true;
        }

        // Other keys: Don't handle (let them pass through)
        return false;
      },
    }));

    // --------------------------------------------------------
    // RENDER: Empty state
    // --------------------------------------------------------
    if (items.length === 0) {
      return (
        <div className="mention-list">
          <div className="mention-list-empty">No users found</div>
        </div>
      );
    }

    // --------------------------------------------------------
    // RENDER: User list
    // --------------------------------------------------------
    return (
      <div className="mention-list">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            className={`mention-list-item ${
              index === selectedIndex ? 'is-selected' : ''
            }`}
            onClick={() => command({ id: item.id, label: item.name })}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            {/* Avatar with fallback to initials */}
            <div
              className="mention-list-avatar"
              style={{
                backgroundColor: item.avatarUrl ? 'transparent' : 'var(--color-border)',
              }}
            >
              {item.avatarUrl ? (
                <img src={item.avatarUrl} alt="" />
              ) : (
                <span>{item.name.charAt(0).toUpperCase()}</span>
              )}
            </div>

            {/* Name and email */}
            <div className="mention-list-content">
              <span className="mention-list-name">{item.name}</span>
              <span className="mention-list-email">{item.email}</span>
            </div>
          </button>
        ))}
      </div>
    );
  }
);

// Set display name for React DevTools
MentionList.displayName = 'MentionList';
